import { useEffect, useState } from 'react';
import { CUSTOM_PASSENGER_TYPE_CODES } from '../../../../helpers/passengerTypes';

/**
 * Custom hook to manage passenger type codes (PTC) selection in a search form.
 *
 * This hook handles:
 * - Managing the selected passenger type codes and their quantities.
 * - Initializing passenger data from search parameters.
 * - Updating form fields based on selected passenger type.
 * - Fetching supported channels for the active passenger type.
 *
 */
const useSearchFormPtc = ({
  formFields,
  passengerTypeCodesList,
  searchForm,
  searchParams,
  onSetPtc,
}) => {
  const [ptcWithQuantity, setPtcWithQuantity] = useState({});

  const hasPtcList = passengerTypeCodesList.length > 0;
  const activePtc = Object.entries(ptcWithQuantity).find(([, value]) => value > 0)?.[0] || null;
  const isPtcSelected = Object.values(ptcWithQuantity).some(value => value > 0);

  const formInitialValue = {
    adult: searchForm?.adults?.initial,
    passengerTypeCode: searchForm?.passengerTypeCode?.initial,
  };

  const getPtcSupportedChannels = () => {
    if (!isPtcSelected) return null;

    return CUSTOM_PASSENGER_TYPE_CODES.find(ptc => ptc.code === activePtc)?.suppliers || null;
  };

  const getPassengerInfo = () => {
    const code =
      searchParams?.passengerTypeCode ||
      searchParams?.passengerCode ||
      formInitialValue.passengerTypeCode;
    const count = ![undefined, null].includes(searchParams?.adults)
      ? searchParams.adults
      : formInitialValue.adult;

    return code ? { code, count } : null;
  };

  const updatePassengerTypeCodeQuantity = (val, code) => {
    setPtcWithQuantity(prev => ({ ...prev, [code]: val }));

    if (val > 0) formFields.adults.onChange(val);
  };

  const initializePassengerData = () => {
    setPtcWithQuantity({});

    const { code, count } = getPassengerInfo() || {};
    const hasPassengerInfo = code && count;

    if (hasPassengerInfo) {
      if (hasPtcList) updatePassengerTypeCodeQuantity(count, code);
      else formFields.adults.onChange(count);
    }
  };

  useEffect(() => {
    initializePassengerData();
  }, [
    formInitialValue.adult,
    formInitialValue.passengerTypeCode,
    searchParams?.adults,
    searchParams?.passengerTypeCode,
  ]);

  useEffect(() => {
    if (hasPtcList) onSetPtc();
  }, []);

  return {
    isPtcSelected,
    activePtc,
    ptcWithQuantity,
    getPtcSupportedChannels,
    updatePassengerTypeCodeQuantity,
  };
};

export default useSearchFormPtc;
